

import React, { useState, useCallback } from "react";
import { exportToExcel, ExportParams } from "./Excelexportutil";

interface ExportButtonProps {
  params:    ExportParams;
  variant?:  "toolbar" | "light";
  label?:    string;
  disabled?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  params,
  variant  = "toolbar",
  label    = "Export to Excel",
  disabled = false,
}) => {
  const [exporting, setExporting] = useState(false);
  const [hovered,   setHovered]   = useState(false);

  const handleExport = useCallback(async () => {
    if (exporting || disabled) return;
    setExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 10)); // allow spinner to paint
      exportToExcel(params);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setTimeout(() => setExporting(false), 800);
    }
  }, [params, exporting, disabled]);

  const isLight    = variant === "light";
  const isDisabled = disabled || exporting;

  const base: React.CSSProperties = {
    display:        "inline-flex",
    alignItems:     "center",
    gap:            "6px",
    padding:        "5px 13px",
    borderRadius:   "8px",
    border:         isLight ? "1px solid #86efac" : "1px solid rgba(134,239,172,0.4)",
    cursor:         isDisabled ? "not-allowed" : "pointer",
    fontSize:       "11px",
    fontWeight:     700,
    letterSpacing:  "0.4px",
    transition:     "all 0.15s ease",
    whiteSpace:     "nowrap",
    flexShrink:     0,
    opacity:        isDisabled ? 0.55 : 1,
    background:     isLight
                      ? (hovered && !isDisabled ? "#f0fdf4" : "#ffffff")
                      : (hovered && !isDisabled ? "rgba(134,239,172,0.18)" : "rgba(134,239,172,0.1)"),
    color:          isLight ? "#166534" : "#bbf7d0",
    boxShadow:      isLight ? "0 1px 3px rgba(15,23,42,0.08)" : "0 1px 4px rgba(0,0,0,0.2)",
  };

  /* ── Icons ──────────────────────────────────────────────────────────────── */
  const ExcelIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
    >
      {/* File icon with table lines inside */}
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="16" y2="17"/>
      <line x1="10" y1="9" x2="14" y2="9"/>
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7,10 12,15 17,10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );

  const SpinnerIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: "expbtn-spin 0.7s linear infinite" }}
    >
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );

  return (
    <>
      <style>{`@keyframes expbtn-spin { to { transform: rotate(360deg); } }`}</style>
      <button
        onClick={handleExport}
        disabled={isDisabled}
        style={base}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title={`Export ${params.heading ?? ""} to Excel`}
        aria-label="Export to Excel"
      >
        {exporting ? <SpinnerIcon /> : <ExcelIcon />}
        <span>{exporting ? "Exporting…" : label}</span>
        {!exporting && <DownloadIcon />}
      </button>
    </>
  );
};

export default ExportButton;